/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright (c) 2017 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

import React, { FunctionComponent } from "react";
import ThemeProvider from "./../ThemeProvider";
import styled, { css } from "styled-components";

enum buttonSizes {
  large = "large",
  medium = "medium",
  small = "small",
}

enum buttonTypes {
  default = "default",
  primary = "primary",
  attention = "attention",
  icon = "icon",
}

interface Props {
  disabled?: boolean;
  size?: buttonSizes;
  varient?: buttonTypes;
  theme?: string;
  active: boolean;
  className?: string;
}

const Button: FunctionComponent<Props> = (props) => {
  const contextLight = {
    main: "light",
  };
  const contextDark = {
    main: "dark",
  };
  const set_main_theme = (context) => {
    if(context === undefined){
      return contextLight
    }else if(context === "light"){
      return contextLight
    }else if(context !== undefined && context.main === undefined){
      return contextDark
    }else if(context !== undefined && context.main !== undefined){
      return context
    }
  };

  return (
    <ThemeProvider.Consumer>
      {(context) => (
        <MainButton
          {...props}
          context={set_main_theme(context)}
          varient={props.varient}
          size={props.size}
          disabled={props.disabled}
          className={props.className}
          active={props.active}
        >
          {props.children}
        </MainButton>
      )}
    </ThemeProvider.Consumer>
  );
};
const MainButton = styled.button`
  & + & {
    margin-left: 12px;
  }
  & > svg[class*="-18"] {
    padding: 6px;
    width: 30px;
    height: 30px;
  }
  & > svg[class*="-24"] {
    padding: 8px;
    width: 40px;
    height: 40px;
  }
  align-items: center;
  background: transparent;
  border-radius: 22px;
  color: #6a7070;
  cursor: pointer;
  display: inline-flex;
  font-family: "Open Sans semibold", Calibri, Tahoma, sans-serif;
  justify-content: center;
  padding: 8px 24px;
  padding: 0 24px 0 24px;
  position: relative;
  @media (prefers-contrast: more) {
    /* styles for high contrast mode */
    border: 2px solid;
  }
  ${(props) =>
    props.disabled === true &&
    css`
      border: none;
      background-color: rgba(217, 217, 217, 0.6);
      color: #6a7070;
      pointer-events: none;
    `}
  ${(props) =>
    props.context.main === "light" &&
    css`
      &:focus {
        outline: ${(props) =>
          props.context.button !== undefined &&
          props.context.button.outline !== undefined
            ? `2px solid ${props.context.button.outline} !important`
            : "2px solid #368de4 !important"};
        outline-offset: 2px;
      }
      ${(props) =>
        props.varient !== "icon" &&
        css`
          ${(props) =>
            props.size === "large" &&
            css`
              font-size: 16px;
              height: ${(props) =>
                props.context.button !== undefined &&
                props.context.button.size !== undefined &&
                props.context.button.size.large !== undefined
                  ? `${props.context.button.size.large} !important`
                  : "40px !important"};
              line-height: 20px;
              min-width: 152px;
            `}
          ${(props) =>
            props.size === "medium" &&
            css`
              font-size: 16px;
              height: ${(props) =>
                props.context.button !== undefined &&
                props.context.button.size !== undefined &&
                props.context.button.size.medium !== undefined
                  ? `${props.context.button.size.medium} !important`
                  : "36px !important"};
              line-height: 20px;
              min-width: 152px;
            `}
          ${(props) =>
            props.size === "small" &&
            css`
              font-size: 14px;
              height: ${(props) =>
                props.context.button !== undefined &&
                props.context.button.size !== undefined &&
                props.context.button.size.small !== undefined
                  ? `${props.context.button.size.small} !important`
                  : "32px !important"};
              line-height: 18px;
              min-width: 128px;
            `}
          ${(props) =>
            props.varient === "default" &&
            css`
              background-color: ${(props) =>
                props.context.button !== undefined &&
                props.context.button.default !== undefined &&
                props.context.button.default.backgourndColor !== undefined
                  ? props.context.button.default.backgourndColor
                  : "rgba(217, 217, 217, 0)"};
              border: 1px solid #6a7070;
              color: rgba(37, 37, 37, 0.9);
              &:hover,
              &:focus {
                background-color: ${(props) =>
                  props.context.button !== undefined &&
                  props.context.button.default !== undefined &&
                  props.context.button.default.hover !== undefined
                    ? props.context.button.default.hover
                    : "rgba(217, 217, 217, 0.3)"};
              }
            `}
        
          ${(props) =>
            props.varient === "primary" &&
            css`
              background-color: ${(props) =>
                props.context.button !== undefined &&
                props.context.button.primary !== undefined &&
                props.context.button.primary.backgourndColor !== undefined
                  ? props.context.button.primary.backgourndColor
                  : "#005d83"};
              color: #ffffff;
              border: none;
              &:hover,
              &:focus {
                background-color: ${(props) =>
                  props.context.button !== undefined &&
                  props.context.button.primary !== undefined &&
                  props.context.button.primary.hover !== undefined
                    ? props.context.button.primary.hover
                    : "#047a9c"};
                color: #ffffff;
              }
            `}
          ${(props) =>
            props.varient === "attention" &&
            css`
              background-color: ${(props) =>
                props.context.button !== undefined &&
                props.context.button.attention !== undefined &&
                props.context.button.attention.backgourndColor !== undefined
                  ? props.context.button.attention.backgourndColor
                  : "#ffcb4e"};
              color: rgba(37, 37, 37, 0.9);
              border: none;
              &:hover,
              &:focus {
                background-color: ${(props) =>
                  props.context.button !== undefined &&
                  props.context.button.attention !== undefined &&
                  props.context.button.attention.hover !== undefined
                    ? props.context.button.attention.hover
                    : "#ffe15c"};
                color: rgba(37, 37, 37, 0.9);
              }
            `}
          ${(props) =>
            props.disabled === true &&
            css`
              border: none;
              background-color: rgba(217, 217, 217, 0.6);
              color: #6a7070;
              pointer-events: none;
            `}
        `}
      ${(props) =>
        props.varient === "icon" &&
        css`
          border: none;
          width: fit-content;
          width: fit-content;
          height: fit-content;
          padding: 0px 0px;
          border-radius: 100%;

          &:hover {
            background-color: rgba(217, 217, 217, 0.65);
          }

          &:focus {
            background-color: rgba(217, 217, 217, 0.65);
            outline: 2px solid #368de4;
            outline-offset: 2px;
          }

          ${(props) =>
            props.disabled === true &&
            css`
              color: rgb(0, 93, 131);
              background-color: rgba(128, 197, 221, 0.3);
              &:hover {
                background-color: rgba(128, 197, 221, 0.5);
              }
              &:focus {
                background-color: rgba(128, 197, 221, 0.5);
                outline: 2px solid #368de4;
                outline-offset: 2px;
              }
              &:disabled {
                background-color: rgba(128, 197, 221, 0.2);
                color: rgba(0, 93, 131, 0.5);
                border: none;
              }
            `}
          ${(props) =>
            props.disabled === true &&
            css`
              background-color: transparent;
              opacity: 60%;
              border: none;
            `}
        `}
    `}

${(props) =>
    props.context.main === "dark" &&
    css`
      &:focus {
        outline: ${(props) =>
          props.context !== undefined &&
          props.context.button !== undefined &&
          props.context.button.outline !== undefined
            ? `2px solid ${props.context.button.outline} !important`
            : "2px solid #64b0fb !important"};
          outline-offset: 2px;
      }
      ${(props) =>
        props.varient !== "icon" &&
        css`
          ${(props) =>
            props.size === "large" &&
            css`
              font-size: 16px;
              height: ${(props) =>
                props.context.button !== undefined &&
                props.context.button.size !== undefined &&
                props.context.button.size.large !== undefined
                  ? `${props.context.button.size.large} !important`
                  : "40px !important"};
              line-height: 20px;
              min-width: 152px;
            `}
          ${(props) =>
            props.size === "medium" &&
            css`
              font-size: 16px;
              height: ${(props) =>
                props.context.button !== undefined &&
                props.context.button.size !== undefined &&
                props.context.button.size.medium !== undefined
                  ? `${props.context.button.size.medium} !important`
                  : "36px !important"};
              line-height: 20px;
              min-width: 152px;
            `}
        ${(props) =>
            props.size === "small" &&
            css`
              font-size: 14px;
              height: ${(props) =>
                props.context.button !== undefined &&
                props.context.button.size !== undefined &&
                props.context.button.size.small !== undefined
                  ? `${props.context.button.size.small} !important`
                  : "32px !important"};
              line-height: 18px;
              min-width: 128px;
            `}
        `}
      ${(props) =>
        props.varient === "default" &&
        css`
          background-color: ${(props) =>
            props.context.button !== undefined &&
            props.context.button.default !== undefined &&
            props.context.button.default.backgourndColor !== undefined
              ? props.context.button.default.backgourndColor
              : "rgba(217, 217, 217, 0)"};
          border: 1px solid #ffffff;
          color: #ffffff;
          &:hover,
          &:focus {
            color: #ffffff;
            background-color: ${(props) =>
              props.context.button !== undefined &&
              props.context.button.default !== undefined &&
              props.context.button.default.hover !== undefined
                ? props.context.button.default.hover
                : "rgba(217, 217, 217, 0.2)"};
          }
        `}
       ${(props) =>
        props.varient === "primary" &&
        css`
          background-color: ${(props) =>
            props.context.button !== undefined &&
            props.context.button.primary !== undefined &&
            props.context.button.primary.backgourndColor !== undefined
              ? props.context.button.primary.backgourndColor
              : "rgba(255, 255, 255, 0.88)"};
          color: rgba(0, 0, 0, 0.9);
          border: none;
          &:hover,
          &:focus {
            background-color: rgba(255, 255, 255, 1);
            background-color: ${(props) =>
              props.context.button !== undefined &&
              props.context.button.primary !== undefined &&
              props.context.button.primary.hover !== undefined
                ? props.context.button.primary.hover
                : "rgba(255, 255, 255, 1)"};
            color: rgba(0, 0, 0, 0.9);
          }
        `}
        ${(props) =>
        props.varient === "attention" &&
        css`
          background-color: ${(props) =>
            props.context.button !== undefined &&
            props.context.button.attention !== undefined &&
            props.context.button.attention.backgourndColor !== undefined
              ? props.context.button.attention.backgourndColor
              : "#ffcb4e"};
          color: rgba(37, 37, 37, 0.9);
          border: none;
          &:hover,
          &:focus {
            background-color: ${(props) =>
              props.context.button !== undefined &&
              props.context.button.attention !== undefined &&
              props.context.button.attention.hover !== undefined
                ? props.context.button.attention.hover
                : "#ffe15c"};
            color: rgba(37, 37, 37, 0.9);
          }
        `}
         ${(props) =>
        props.disabled === true &&
        css`
          border: none;
          background-color: rgba(217, 217, 217, 0.6);
          color: #6a7070;
          pointer-events: none;
        `}
        ${(props) =>
        props.varient === "icon" &&
        css`
          border: none;
          color: rgb(255, 255, 255);
          width: fit-content;
          height: fit-content;
          padding: 0px 0px;
          border-radius: 100%;
          &:hover {
            background-color: rgba(0, 0, 0, 0.8);
            color: rgba(255, 255, 255, 1);
          }

          &:focus {
            background-color: rgba(0, 0, 0, 0.8);
            outline: 2px solid #368de4;
            outline-offset: 2px;
          }

          ${(props) =>
            props.disabled === true &&
            css`
              color: rgb(0, 93, 131) !important;
              background-color: rgba(255, 255, 255, 0.8);
              &:hover {
                background-color: rgba(255, 255, 255, 0.95);
              }
              &:focus {
                background-color: rgba(255, 255, 255, 0.95);
                outline: 2px solid #64b0fb;
                outline-offset: 2px;
              }
              &:disabled {
                background-color: rgba(255, 255, 255, 0.4);
                color: rgba(0, 93, 131, 0.7);
                opacity: 1;
                border: none;
              }
            `}
          ${(props) =>
            props.disabled === true &&
            css`
              color: rgb(255, 255, 255);
              background-color: transparent;
              opacity: 60%;
              border: none;
            `}
        `}
    `}
`;

Button.defaultProps = {
  varient: buttonTypes.default,
  size: buttonSizes.medium,
  theme: "light",
  active: false,
};

export default Button;
